import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  AppInformationQuery,
  BanUserMutation,
  BanUserMutationVariables,
  ForceUserTestMutation,
  ForceUserTestMutationVariables,
  GetAbTestsConfigsQuery,
  GetUserLastSessionQuery,
  LiveOpsContentQuery,
  SearchUserQuery,
  UnbanUserMutation,
  UnbanUserMutationVariables,
  UpdateUserMutation,
  UpdateUserMutationVariables,
} from '../../../../../__gqltypes__';
import { AbTestStatus, ForceUpdateUserInput } from '../../../../../__gqltypes__';
import { GET_ABTESTS_CONFIGS } from '../../../liveops/screens/ABTests/graphql';
import {
  APP_INFORMATION,
  BAN_USER_MUTATION,
  FORCE_USER_ABTEST_MUTATION,
  GET_USER_LAST_SESSION,
  LIVE_OPS_CONTENT,
  RESET_USER_SUBSCRIPTIONS_MUTATION,
  SAVE_USER_PLAYLIST_PROGRESS_ADMIN,
  SEARCH_USER,
  SET_USER_CURRENT_BASIC_CONTENT_PACK_ADMIN,
  SET_SUBSCRIPTION_EXPIRES_MUTATION,
  UNBAN_USER_MUTATION,
  UNSAVE_USER_PLAYLIST_ADMIN,
  UPDATE_USER,
  UPDATE_USER_PLAYLIST_PROGRESS_ADMIN,
  USER_PLAYLIST_PROGRESS_ADMIN,
} from '../../graphql';
import {
  formatUserItems,
  getItemsNotOwnedByUser,
  MAX_COUNT_PREVIOUSLY_SEARCH_USER_LOCAL_STORAGE,
  PREVIOUSLY_USER_SEARCH_LOCAL_STORAGE_KEY,
} from './utils';
import { PreviouslySearchedType } from './typedefs';

type SearchedUser = NonNullable<SearchUserQuery['user']>;
type LiveOps = LiveOpsContentQuery['liveOps'];
type AbTestConfig = GetAbTestsConfigsQuery['liveOps']['ABTestConfigs'][0];
type SetSubscriptionExpiresMutation = {
  setSubscriptionExpires?: {
    user?: SearchUserQuery['user'] | null;
  } | null;
};
type SetSubscriptionExpiresMutationVariables = {
  input: {
    userId: string;
    expires: string;
  };
};

const UPDATE_STATUS = {
  FAILED: 'failed',
  SUCCEED: 'succeed',
};

export const PARAM_TYPES = {
  INT: 'int',
  INTORNULL: 'int-or-null',
  FLOAT: 'float',
  STRING: 'string',
  FRAGMENT: 'fragment',
  META_FRAGMENTS: 'metaFragments',
  PLAYLIST: 'playlist',
  PLAYLIST_MULTI: 'playlist-multi',
  MONTHLY_PASS: 'monthlypass',
  STICKERS: 'stickers',
  APP_SKINS: 'appSkins',
  PROFILE_FRAMES: 'profileFrames',
  ENERGY: 'energy',
  EARNED_CONTENT_PACK_IDS: 'earnedContentPackIds',
  PIGGY_BANK: 'piggyBank',
  SHARDS: 'shards',
  CALENDARS: 'calendars',
} as const;

export type ParamTypeValue = typeof PARAM_TYPES[keyof typeof PARAM_TYPES];
type EditableParamConfig = {
  key: string;
  type: ParamTypeValue;
  customPath?: string;
  min?: number;
};

const NON_NEGATIVE_FIELDS = new Set([
  'coinBalance',
  'diamondBalance',
  'tournamentTicketBalance',
  'boostRemove2Balance',
  'boostTeamSpiritBalance',
  'boostReviveBalance',
  'boostNinjaBalance',
  'boostBombBalance',
  'boostSpeedsterBalance',
  'boostArtistOnlyBalance',
  'boostHurdleHintBalance',
  'unlimitedBoosts.unlimitedBoostRemove2Balance',
  'unlimitedBoosts.unlimitedBoostSpeedsterBalance',
]);

const clampNonNegative = (value: number | undefined) =>
  typeof value === 'number' ? Math.max(0, value) : value;

export const EDITABLE_PARAMS = [
  { key: 'userLevel', type: PARAM_TYPES.INT },
  { key: 'userProgressToNextLevel', type: PARAM_TYPES.FLOAT },
  { key: 'coinBalance', type: PARAM_TYPES.INT, min: 0 },
  { key: 'diamondBalance', type: PARAM_TYPES.INT, min: 0 },
  { key: 'claimedUpdateGiftId', type: PARAM_TYPES.STRING },
  { key: 'premiumDays', type: PARAM_TYPES.INT },
  { key: 'vipDays', type: PARAM_TYPES.INT },
  { key: 'tournamentTicketBalance', type: PARAM_TYPES.INT, min: 0 },
  { key: 'boostRemove2Balance', type: PARAM_TYPES.INT, min: 0 },
  { key: 'boostTeamSpiritBalance', type: PARAM_TYPES.INT, min: 0 },
  { key: 'boostReviveBalance', type: PARAM_TYPES.INT, min: 0 },
  { key: 'boostNinjaBalance', type: PARAM_TYPES.INT, min: 0 },
  { key: 'boostBombBalance', type: PARAM_TYPES.INT, min: 0 },
  { key: 'boostSpeedsterBalance', type: PARAM_TYPES.INT, min: 0 },
  { key: 'boostArtistOnlyBalance', type: PARAM_TYPES.INT, min: 0 },
  { key: 'boostHurdleHintBalance', type: PARAM_TYPES.INT, min: 0 },
  { key: 'unlimitedBoosts.unlimitedBoostRemove2Balance', type: PARAM_TYPES.INT, min: 0 },
  { key: 'unlimitedBoosts.unlimitedBoostSpeedsterBalance', type: PARAM_TYPES.INT, min: 0 },
  { key: 'stampCards', type: PARAM_TYPES.INT },
  { key: 'totalStampCardsEarned', type: PARAM_TYPES.INT },
  { key: 'energyCoinSupply', type: PARAM_TYPES.ENERGY },
  { key: 'energyXPSupply', type: PARAM_TYPES.ENERGY },
  { key: 'energyTournamentTicketSupply', type: PARAM_TYPES.ENERGY },
  { key: 'energyLiveEventTicketSupply', type: PARAM_TYPES.ENERGY },
  { key: 'liveEventCurrency', type: PARAM_TYPES.INTORNULL },
  {
    key: 'metaFragments',
    type: PARAM_TYPES.META_FRAGMENTS,
    config: [
      { key: 'BRONZE', type: PARAM_TYPES.INT },
      { key: 'SILVER', type: PARAM_TYPES.INT },
      { key: 'GOLD', type: PARAM_TYPES.INT },
    ],
  },
  { key: 'accoladeBalance', type: PARAM_TYPES.INT },
  {
    key: 'shards',
    type: PARAM_TYPES.SHARDS,
    config: [
      { key: 'BRONZE', type: PARAM_TYPES.INT },
      { key: 'SILVER', type: PARAM_TYPES.INT },
      { key: 'GOLD', type: PARAM_TYPES.INT },
    ],
  },
  {
    key: 'fragmentBalance',
    type: PARAM_TYPES.FRAGMENT,
    isArray: true,
    config: [
      { key: 'id', type: PARAM_TYPES.STRING },
      { key: 'amount', type: PARAM_TYPES.INT },
    ],
  },
  {
    key: 'basicTierContentPack',
    type: PARAM_TYPES.PLAYLIST,
    config: [
      { key: 'id', type: PARAM_TYPES.STRING, customPath: 'contentPack.id' },
      { key: 'level', type: PARAM_TYPES.INT },
      { key: 'progressToNextLevel', type: PARAM_TYPES.FLOAT },
    ],
  },
  {
    key: 'stickers',
    type: PARAM_TYPES.STICKERS,
    isArray: true,
    config: [
      { key: 'id', type: PARAM_TYPES.STRING },
      { key: 'level', type: PARAM_TYPES.INT },
    ],
  },
  {
    key: 'appSkins',
    type: PARAM_TYPES.APP_SKINS,
    isArray: true,
    config: [
      { key: 'id', type: PARAM_TYPES.STRING },
      { key: 'level', type: PARAM_TYPES.INT },
    ],
  },
  {
    key: 'profileFrames',
    type: PARAM_TYPES.PROFILE_FRAMES,
    isArray: true,
    config: [
      { key: 'id', type: PARAM_TYPES.STRING },
      { key: 'level', type: PARAM_TYPES.INT },
    ],
  },
  {
    key: 'earnedContentPackIds',
    type: PARAM_TYPES.EARNED_CONTENT_PACK_IDS,
    isArray: true,
  },
  {
    key: 'currentUserMonthlyPass',
    type: PARAM_TYPES.MONTHLY_PASS,
    config: [
      { key: 'progressToNextLevel', type: PARAM_TYPES.FLOAT },
      { key: 'level', type: PARAM_TYPES.INT },
    ],
  },
  {
    key: 'piggyBank',
    type: PARAM_TYPES.PIGGY_BANK,
    config: [
      { key: 'diamonds', type: PARAM_TYPES.INT },
      { key: 'coins', type: PARAM_TYPES.INT },
    ],
  },
  {
    key: 'freeSpins',
    type: PARAM_TYPES.INT,
  },
  {
    key: 'calendars',
    type: PARAM_TYPES.CALENDARS,
    isArray: true,
    config: [
      { key: 'id', type: PARAM_TYPES.STRING },
      { key: 'name', type: PARAM_TYPES.STRING },
    ],
  },
] as const;

type FormInput = {
  id: string;
  userLevel: number;
  userProgressToNextLevel?: number;
  coinBalance?: number;
  diamondBalance?: number;
  claimedUpdateGiftId?: string;
  premiumDays?: number;
  vipDays?: number;
  tournamentTicketBalance?: number;
  boostRemove2Balance?: number;
  boostTeamSpiritBalance?: number;
  boostReviveBalance?: number;
  boostNinjaBalance?: number;
  boostBombBalance?: number;
  boostSpeedsterBalance?: number;
  boostArtistOnlyBalance?: number;
  boostHurdleHintBalance?: number;
  unlimitedBoosts?: {
    unlimitedBoostRemove2Balance?: number;
    unlimitedBoostSpeedsterBalance?: number;
  };
  stampCards?: number;
  totalStampCardsEarned: number;
  energyCoinSupply?: number;
  energyXPSupply?: number;
  energyTournamentTicketSupply?: number;
  energyLiveEventTicketSupply?: number;
  liveEventCurrency?: number | null;
  fragmentBalance: { id: string; amount: number }[];
  basicTierContentPack?: {
    id: string;
    level: number;
    progressToNextLevel: number;
    totalXP?: number;
    currentMonthXP?: number;
    accuracy?: number;
    highestScore?: number;
    averageScore?: number;
    roundPlayedCount?: number;
    contentDiscoveredCount?: number;
    totalContentItemsCount?: number;
    contentPack: { id: string; name: string; picture?: string | null };
  };
  savedContentPacks: {
    id: string;
    level: number;
    progressToNextLevel: number;
    totalXP?: number;
    currentMonthXP?: number;
    accuracy?: number;
    highestScore?: number;
    averageScore?: number;
    roundPlayedCount?: number;
    contentDiscoveredCount?: number;
    totalContentItemsCount?: number;
    contentPack: { id: string; name: string; picture?: string | null };
  }[];
  recentContentPacks: {
    id: string;
    level: number;
    progressToNextLevel: number;
    totalXP?: number;
    currentMonthXP?: number;
    accuracy?: number;
    highestScore?: number;
    averageScore?: number;
    roundPlayedCount?: number;
    contentDiscoveredCount?: number;
    totalContentItemsCount?: number;
    contentPack: { id: string; name: string; picture?: string | null };
  }[];
  stickers: { id: string; level: number }[];
  appSkins: { id: string; level: number }[];
  profileFrames: { id: string; level: number }[];
  currentUserMonthlyPass?: { progressToNextLevel: number; level: number };
  earnedContentPackIds?: string[];
  piggyBank: { coins: number; diamonds: number };
  metaFragments: { BRONZE?: number; SILVER?: number; GOLD?: number };
  shards: { BRONZE?: number; SILVER?: number; GOLD?: number };
  accoladeBalance?: number;
  team: { id: string };
  freeSpins?: number;
  calendars: { id: string; name: string }[];
};
type UpdatedUser = SearchedUser;
type UserPlaylistProgress = NonNullable<FormInput['basicTierContentPack']>;
type UserPlaylistProgressRaw = Partial<Omit<UserPlaylistProgress, 'contentPack'>> & {
  contentPack: { id: string; name?: string | null; picture?: string | null };
};
type SearchedUserWithAdminPlaylistProgress = SearchedUser & {
  claimedUpdateGiftId?: string | null;
  savedContentPacks?: { list?: UserPlaylistProgressRaw[] | null } | null;
  recentContentPacks?: { list?: UserPlaylistProgressRaw[] | null } | null;
};

const mapUserPlaylistProgress = (p: UserPlaylistProgressRaw): UserPlaylistProgress => ({
  id: p.id ?? p.contentPack.id,
  level: p.level ?? 1,
  progressToNextLevel: p.progressToNextLevel ?? 0,
  totalXP: p.totalXP,
  currentMonthXP: p.currentMonthXP,
  accuracy: p.accuracy,
  highestScore: p.highestScore,
  averageScore: p.averageScore,
  roundPlayedCount: p.roundPlayedCount,
  contentDiscoveredCount: p.contentDiscoveredCount,
  totalContentItemsCount: p.totalContentItemsCount,
  contentPack: {
    id: p.contentPack.id,
    name: p.contentPack.name ?? p.contentPack.id,
    picture: p.contentPack.picture ?? null,
  },
});

const formatPlaylistSaveMessage = (before: UserPlaylistProgress, after: UserPlaylistProgress) => {
  const fields: { label: string; key: keyof UserPlaylistProgress }[] = [
    { label: 'level', key: 'level' },
    { label: 'progress', key: 'progressToNextLevel' },
    { label: 'total XP', key: 'totalXP' },
    { label: 'month XP', key: 'currentMonthXP' },
    { label: 'rounds', key: 'roundPlayedCount' },
    { label: 'accuracy', key: 'accuracy' },
    { label: 'avg', key: 'averageScore' },
    { label: 'best', key: 'highestScore' },
  ];
  const changes = fields
    .filter(({ key }) => before[key] !== after[key])
    .map(({ label, key }) => `${label} ${before[key] ?? '-'} -> ${after[key] ?? '-'}`);

  return `${after.contentPack.name || after.contentPack.id}: ${changes.length ? changes.join(', ') : 'no stat changes'}`;
};

const toPlaylistProgressAdminInput = (userId: string, playlist: UserPlaylistProgress) => ({
  userId,
  contentPackId: playlist.contentPack.id,
  level: _.toInteger(playlist.level),
  progressToNextLevel: _.toNumber(playlist.progressToNextLevel),
  totalXP: _.isNil(playlist.totalXP) ? undefined : _.toInteger(playlist.totalXP),
  currentMonthXP: _.isNil(playlist.currentMonthXP) ? undefined : _.toInteger(playlist.currentMonthXP),
  roundPlayedCount: _.isNil(playlist.roundPlayedCount) ? undefined : _.toInteger(playlist.roundPlayedCount),
  accuracy: _.isNil(playlist.accuracy) ? undefined : _.toNumber(playlist.accuracy),
  averageScore: _.isNil(playlist.averageScore) ? undefined : _.toInteger(playlist.averageScore),
  highestScore: _.isNil(playlist.highestScore) ? undefined : _.toInteger(playlist.highestScore),
});

const convertToMutationInput = (user: FormInput): ForceUpdateUserInput => {
  const cast = (value: number | string | undefined, type: ParamTypeValue) => {
    let _value: number | string | undefined = value;
    switch (type) {
      case PARAM_TYPES.INT:
      case PARAM_TYPES.ENERGY:
        _value = value ? _.toInteger(value) : 0;
        break;
      case PARAM_TYPES.FLOAT:
        _value = value ? _.toNumber(value) : 0;
        break;
      case PARAM_TYPES.INTORNULL:
        _value = value ? _.toInteger(value) : undefined;
        break;
      default:
        break;
    }
    return _value as number | undefined;
  };
  const castObject = (object: Record<string, string | number> | undefined, config: readonly EditableParamConfig[]) => {
    if (object === undefined) return undefined;
    const paramResult: Record<string, number | undefined> = {};
    config.forEach((item) => {
      if (item.customPath) {
        paramResult[item.key] = cast(_.get(object, item.customPath), item.type);
      } else {
        paramResult[item.key] = cast(object[item.key], item.type);
      }
    });
    return paramResult;
  };

  // After the loop it should be an Omit<ForceUpdateUserInput, 'id'>, but not yet
  const res: Partial<ForceUpdateUserInput> = {};
  EDITABLE_PARAMS.forEach((param) => {
    if ('config' in param && user[param.key]) {
      if ('isArray' in param) {
        _.set(
          res,
          param.key,

          // @ts-ignore element may have nested objects (e.g. contentPack) consumed via customPath
          (user[param.key] || []).map((element) => castObject(element, param.config))
        );
      } else {
        // param.key may be a nested path here (unlimitedBoosts.unlimitedBoostRemove2Balance)
        // @ts-ignore value may include nested objects (e.g. contentPack) consumed via customPath
        _.set(res, param.key, castObject(_.get(user, param.key), param.config));
      }
    } else {
      let castValue = cast(_.get(user, param.key), param.type);
      if (NON_NEGATIVE_FIELDS.has(param.key)) {
        castValue = clampNonNegative(castValue);
      }
      _.set(res, param.key, castValue);
    }
  });
  return { id: user.id, ...(res as Omit<ForceUpdateUserInput, 'id'>), savedContentPacks: [] };
};

export const useUserView = () => {
  /** SEARCH */
  const [input, setInput] = useState('');
  const [searchUser, { data: searchData, called: searchUserCalled, loading: getUserLoading }] =
    useLazyQuery<SearchUserQuery>(SEARCH_USER);
  // @ts-ignore
  const handleInputChange = (event) => setInput(event.target.value);

  /** LOAD */
  const [getABTestsConfigs, { data: abTestConfigsData }] = useLazyQuery<GetAbTestsConfigsQuery>(GET_ABTESTS_CONFIGS);
  const [getLastSession, { data: sessionData, loading: lastSessionLoading, called: lastSessionCalled }] =
    useLazyQuery<GetUserLastSessionQuery>(GET_USER_LAST_SESSION);

  const { data: capsData, loading: appInfoLoading } = useQuery<AppInformationQuery>(APP_INFORMATION, {
    fetchPolicy: 'no-cache',
  });
  const { contentCategories } = capsData ? capsData.app : { contentCategories: [] };
  const energyCaps = useMemo(() => {
    const _energyCaps: Record<string, { coin: number; xp: number; tournamentTicket: number; liveEventTicket: number }> =
      {};
    if (capsData) {
      ['basic', 'ad_free', 'premium', 'vip', 'plus'].forEach((tier) => {
        _energyCaps[tier.toUpperCase()] = {
          // @ts-ignore
          coin: capsData.me.energySystems.coin.caps[tier]?.maxStorage ?? 0,
          // @ts-ignore
          xp: capsData.me.energySystems.xp.caps[tier]?.maxStorage ?? 0,
          // @ts-ignore
          tournamentTicket: capsData.me.energySystems.tournamentTicket.caps[tier]?.maxStorage ?? 0,
          // @ts-ignore
          liveEventTicket: capsData.me.energySystems.liveEventTicket.caps[tier]?.maxStorage ?? 0,
        };
      });
      return _energyCaps;
    }
    return {};
  }, [capsData]);

  const { data: liveOpsData } = useQuery<LiveOpsContentQuery>(LIVE_OPS_CONTENT);
  const {
    stickers,
    appskins: appSkins,
    profileFrames,
  }: LiveOps = liveOpsData
    ? liveOpsData.liveOps
    : // @ts-ignore
      ({ stickers: [], appskins: [], profileFrames: [], __typename: 'LiveOps' } as LiveOps);

  /** UPDATE */
  // @ts-ignore
  const [formInput, setFormInput] = useState<FormInput>(null);
  const [updateStatus, setUpdateStatus] = useState<string | null>(null);
  const [user, setUser] = useState<UpdatedUser | null>(null);
  const [searchedPlaylist, setSearchedPlaylist] = useState<UserPlaylistProgress | null>(null);
  const [playlistUpdateMessage, setPlaylistUpdateMessage] = useState<string | null>(null);
  // @ts-ignore
  const [abTestConfigs, setAbTestConfigs] = useState<AbTestConfig[]>(null);
  const [updateUser, { loading: updateLoading }] = useMutation<UpdateUserMutation, UpdateUserMutationVariables>(
    UPDATE_USER,
    {
      onCompleted: () => {
        setUpdateStatus(UPDATE_STATUS.SUCCEED);
      },
      onError: () => {
        setUpdateStatus(UPDATE_STATUS.FAILED);
      },
    }
  );
  const [saveUserPlaylistProgressAdmin, { loading: savePlaylistLoading }] = useMutation(
    SAVE_USER_PLAYLIST_PROGRESS_ADMIN,
    {
      onError: () => setUpdateStatus(UPDATE_STATUS.FAILED),
    }
  );
  const [setUserCurrentBasicContentPackAdmin, { loading: setCurrentBasicContentPackLoading }] = useMutation(
    SET_USER_CURRENT_BASIC_CONTENT_PACK_ADMIN,
    {
      onError: () => setUpdateStatus(UPDATE_STATUS.FAILED),
    }
  );
  const [updateUserPlaylistProgressAdmin, { loading: updatePlaylistLoading }] = useMutation(
    UPDATE_USER_PLAYLIST_PROGRESS_ADMIN,
    {
      onError: () => setUpdateStatus(UPDATE_STATUS.FAILED),
    }
  );
  const [unsaveUserPlaylistAdmin, { loading: unsavePlaylistLoading }] = useMutation(UNSAVE_USER_PLAYLIST_ADMIN, {
    onError: () => setUpdateStatus(UPDATE_STATUS.FAILED),
  });
  const [searchUserPlaylistProgressAdmin, { loading: searchPlaylistLoading }] = useLazyQuery(
    USER_PLAYLIST_PROGRESS_ADMIN,
    {
      onCompleted: (data) => {
        if (data?.userPlaylistProgressAdmin) {
          setSearchedPlaylist(mapUserPlaylistProgress(data.userPlaylistProgressAdmin));
        }
      },
      onError: () => setUpdateStatus(UPDATE_STATUS.FAILED),
    }
  );
  const [banUser, { loading: banLoading }] = useMutation<BanUserMutation, BanUserMutationVariables>(BAN_USER_MUTATION);
  const [unbanUser, { loading: unbanLoading }] = useMutation<UnbanUserMutation, UnbanUserMutationVariables>(
    UNBAN_USER_MUTATION
  );
  const [resetUserSubscriptions, { loading: resetSubscriptionsLoading }] = useMutation(
    RESET_USER_SUBSCRIPTIONS_MUTATION,
    {
      onCompleted: (_data) => {
        handleUserUpdated(_data?.resetUserSubscriptions?.user ?? null);
      },
    }
  );
  const [setSubscriptionExpires, { loading: updateSubscriptionExpiresLoading }] = useMutation<
    SetSubscriptionExpiresMutation,
    SetSubscriptionExpiresMutationVariables
  >(SET_SUBSCRIPTION_EXPIRES_MUTATION, {
    onCompleted: (_data) => {
      handleUserUpdated(_data?.setSubscriptionExpires?.user ?? null);
      setUpdateStatus(UPDATE_STATUS.SUCCEED);
    },
    onError: () => {
      setUpdateStatus(UPDATE_STATUS.FAILED);
    },
  });

  const saveCurrentSearchLocally = (searchQuery: SearchUserQuery) => {
    const searchedUserFromQuery = searchQuery?.user;
    if (_.isNil(searchedUserFromQuery)) {
      return;
    }

    const cachedSearches = localStorage.getItem(PREVIOUSLY_USER_SEARCH_LOCAL_STORAGE_KEY);
    const cacheData: PreviouslySearchedType[] = cachedSearches ? JSON.parse(cachedSearches) : [];

    const cachedUserIndex = cacheData.findIndex((cache) => cache.userId === searchedUserFromQuery.id);
    if (cachedUserIndex >= 0) {
      cacheData.splice(cachedUserIndex, 1);
    }

    cacheData.unshift({
      userId: searchedUserFromQuery.id,
      username: searchedUserFromQuery.displayName,
    });
    cacheData.length = Math.min(cacheData.length, MAX_COUNT_PREVIOUSLY_SEARCH_USER_LOCAL_STORAGE);

    localStorage.setItem(PREVIOUSLY_USER_SEARCH_LOCAL_STORAGE_KEY, JSON.stringify(cacheData));
  };

  const handleUserUpdated = useCallback((_user) => {
    let updatedUser: UpdatedUser | null = null;
    if (_user) {
      if (_user.userLevelProgressToNextLevel) {
        updatedUser = { ..._user, userProgressToNextLevel: _user.userLevelProgressToNextLevel };
      } else {
        updatedUser = _user;
      }
    }
    const _earnedContentPackIds = updatedUser?.ownedContentPacks.list.map(({ contentPack }) => contentPack.id);
    setUser(updatedUser);
    setFormInput((_formInput) => ({
      ..._formInput,
      // @ts-ignore
      premiumDays: updatedUser.premiumDays,
      // @ts-ignore
      vipDays: updatedUser.vipDays,
      // @ts-ignore
      earnedContentPackIds: _earnedContentPackIds,
    }));
  }, []);
  const [forceUserABTest] = useMutation<ForceUserTestMutation, ForceUserTestMutationVariables>(
    FORCE_USER_ABTEST_MUTATION
  );

  /** HANDLERS */
  const handleSearch = async () => {
    setUpdateStatus(null);
    let type = 'BY_ID';
    if (input.includes('@')) {
      type = 'BY_EMAIL';
    } else if (input.includes('+')) {
      type = 'BY_PHONE';
    } else if (input.length !== 36) {
      type = 'BY_NAME';
    }

    searchUser({ variables: { input, type } });
    getABTestsConfigs();
  };
  const handleSaveUser = () => {
    updateUser({ variables: { input: convertToMutationInput(formInput) } });
  };
  const handleBanUser = (userId: string) => {
    banUser({ variables: { input: { userId } } });
  };
  const handleUnbanUser = (userId: string) => {
    unbanUser({ variables: { input: { userId } } });
  };
  const handleResetUserSubscriptions = (userId: string) => {
    resetUserSubscriptions({ variables: { input: { userId } } });
  };
  const handleUpdateSubscriptionExpires = async (userId: string, expires: string) => {
    setUpdateStatus(null);
    await setSubscriptionExpires({ variables: { input: { userId, expires } } });
  };
  const handleForceABTest = (userId: string, testId: string, variation: string) => {
    return forceUserABTest({ variables: { input: { userId, testId, variation } } });
  };
  // @ts-ignore
  const handleChangeUser = (key: string) => (event) => {
    const { value } = event.target;
    setFormInput({ ...formInput, [key]: value });
  };
  const handleChangeEarnedContentPackIds = async (earnedContentPackIds: string[]) => {
    setFormInput({ ...formInput, earnedContentPackIds });
    await updateUser({ variables: { input: convertToMutationInput({ ...formInput, earnedContentPackIds }) } });
  };
  const handleChangeBasicPlaylist = (newPlaylist: {
    id: string;
    level: number;
    progressToNextLevel: number;
    totalXP?: number;
    currentMonthXP?: number;
    accuracy?: number;
    highestScore?: number;
    averageScore?: number;
    roundPlayedCount?: number;
    contentDiscoveredCount?: number;
    totalContentItemsCount?: number;
    contentPack: { id: string; name: string; picture?: string | null };
  }) => {
    setFormInput({ ...formInput, basicTierContentPack: newPlaylist });
  };
  const handleSaveBasicPlaylist = async (playlist: UserPlaylistProgress) => {
    const result = await updateUserPlaylistProgressAdmin({
      variables: {
        input: toPlaylistProgressAdminInput(formInput.id, playlist),
      },
    });
    const userContentPack = result.data?.updateUserPlaylistProgressAdmin?.userContentPack;
    if (userContentPack) {
      const updated = mapUserPlaylistProgress(userContentPack);
      setFormInput({ ...formInput, basicTierContentPack: updated });
      setPlaylistUpdateMessage(formatPlaylistSaveMessage(playlist, updated));
      setUpdateStatus(UPDATE_STATUS.SUCCEED);
    }
  };
  const handleChangeSavedPlaylist = (updated: {
    id: string;
    level: number;
    progressToNextLevel: number;
    totalXP?: number;
    currentMonthXP?: number;
    accuracy?: number;
    highestScore?: number;
    averageScore?: number;
    roundPlayedCount?: number;
    contentDiscoveredCount?: number;
    totalContentItemsCount?: number;
    contentPack: { id: string; name: string; picture?: string | null };
  }) => {
    const next = (formInput.savedContentPacks || []).map((p) =>
      p.contentPack.id === updated.contentPack.id ? { ...p, ...updated } : p
    );
    setFormInput({ ...formInput, savedContentPacks: next });
  };
  const handleSaveSavedPlaylist = async (playlist: UserPlaylistProgress) => {
    const result = await updateUserPlaylistProgressAdmin({
      variables: {
        input: toPlaylistProgressAdminInput(formInput.id, playlist),
      },
    });
    const userContentPack = result.data?.updateUserPlaylistProgressAdmin?.userContentPack;
    if (userContentPack) {
      const updated = mapUserPlaylistProgress(userContentPack);
      const next = (formInput.savedContentPacks || []).map((p) =>
        p.contentPack.id === updated.contentPack.id ? updated : p
      );
      setFormInput({ ...formInput, savedContentPacks: next });
      setPlaylistUpdateMessage(formatPlaylistSaveMessage(playlist, updated));
      setUpdateStatus(UPDATE_STATUS.SUCCEED);
    }
  };
  const handleChangeRecentPlaylist = (updated: UserPlaylistProgress) => {
    const next = (formInput.recentContentPacks || []).map((p) =>
      p.contentPack.id === updated.contentPack.id ? { ...p, ...updated } : p
    );
    setFormInput({ ...formInput, recentContentPacks: next });
  };
  const handleSaveRecentPlaylist = async (playlist: UserPlaylistProgress) => {
    const result = await updateUserPlaylistProgressAdmin({
      variables: {
        input: toPlaylistProgressAdminInput(formInput.id, playlist),
      },
    });
    const userContentPack = result.data?.updateUserPlaylistProgressAdmin?.userContentPack;
    if (userContentPack) {
      const updated = mapUserPlaylistProgress(userContentPack);
      const next = (formInput.recentContentPacks || []).map((p) =>
        p.contentPack.id === updated.contentPack.id ? updated : p
      );
      setFormInput({ ...formInput, recentContentPacks: next });
      setPlaylistUpdateMessage(formatPlaylistSaveMessage(playlist, updated));
      setUpdateStatus(UPDATE_STATUS.SUCCEED);
    }
  };
  const handleChangeSearchedPlaylist = (updated: UserPlaylistProgress) => {
    setSearchedPlaylist(updated);
  };
  const handleSaveSearchedPlaylist = async (playlist: UserPlaylistProgress) => {
    const result = await updateUserPlaylistProgressAdmin({
      variables: {
        input: toPlaylistProgressAdminInput(formInput.id, playlist),
      },
    });
    const userContentPack = result.data?.updateUserPlaylistProgressAdmin?.userContentPack;
    if (userContentPack) {
      const updated = mapUserPlaylistProgress(userContentPack);
      setSearchedPlaylist(updated);
      setPlaylistUpdateMessage(formatPlaylistSaveMessage(playlist, updated));
      setUpdateStatus(UPDATE_STATUS.SUCCEED);
    }
  };
  const handleSetSearchedPlaylistAsCurrentBasic = async (playlist: UserPlaylistProgress) => {
    const result = await setUserCurrentBasicContentPackAdmin({
      variables: {
        input: {
          userId: formInput.id,
          contentPackId: playlist.contentPack.id,
        },
      },
    });
    const userContentPack = result.data?.setUserCurrentBasicContentPackAdmin?.userContentPack;
    if (userContentPack) {
      const updated = mapUserPlaylistProgress(userContentPack);
      setFormInput({ ...formInput, basicTierContentPack: updated });
      setSearchedPlaylist(updated);
      setPlaylistUpdateMessage(`${updated.contentPack.name || updated.contentPack.id}: set as current basic content pack`);
      setUpdateStatus(UPDATE_STATUS.SUCCEED);
    }
  };
  const handleSearchPlaylist = async (contentPackId: string) => {
    const trimmed = contentPackId.trim();
    if (!trimmed) return;
    await searchUserPlaylistProgressAdmin({
      variables: {
        input: {
          userId: formInput.id,
          contentPackId: trimmed,
        },
      },
    });
  };
  const handleDeleteSavedPlaylist = async (contentPackId: string) => {
    await unsaveUserPlaylistAdmin({ variables: { input: { userId: formInput.id, contentPackId } } });
    const next = (formInput.savedContentPacks || []).filter((p) => p.contentPack.id !== contentPackId);
    setFormInput({ ...formInput, savedContentPacks: next });
    setPlaylistUpdateMessage(`${contentPackId}: removed from saved playlists`);
    setUpdateStatus(UPDATE_STATUS.SUCCEED);
  };
  const handleAddSavedPlaylist = async (contentPackId: string) => {
    const trimmed = contentPackId.trim();
    if (!trimmed) return;
    const existing = formInput.savedContentPacks || [];
    if (existing.some((p) => p.contentPack.id === trimmed)) return;
    const result = await saveUserPlaylistProgressAdmin({
      variables: {
        input: {
          userId: formInput.id,
          contentPackId: trimmed,
          level: 1,
          progressToNextLevel: 0,
        },
      },
    });
    const userContentPack = result.data?.saveUserPlaylistProgressAdmin?.userContentPack;
    if (userContentPack) {
      const added = mapUserPlaylistProgress(userContentPack);
      setFormInput({ ...formInput, savedContentPacks: [...existing, added] });
      setPlaylistUpdateMessage(`${added.contentPack.name || added.contentPack.id}: added to saved playlists`);
      setUpdateStatus(UPDATE_STATUS.SUCCEED);
    }
  };
  const handleChangeMonthlyPass = (newUserMonthly: { progressToNextLevel: number; level: number }) => {
    setFormInput({ ...formInput, currentUserMonthlyPass: newUserMonthly });
  };
  const handleChangePiggyBank = (newPiggyBank: { coins: number; diamonds: number }) => {
    setFormInput({ ...formInput, piggyBank: newPiggyBank });
  };
  const handleChangeMetaFragments = (newMeta: { BRONZE?: number; SILVER?: number; GOLD?: number }) => {
    setFormInput({ ...formInput, metaFragments: newMeta });
  };
  const handleChangeShards = (newShards: { BRONZE?: number; SILVER?: number; GOLD?: number }) => {
    setFormInput({ ...formInput, shards: newShards });
  };
  const handleChangeFragment = (newFragment: { id: string; amount: number }, isNew: boolean) => {
    let newFragments = [...formInput.fragmentBalance];
    if (isNew) {
      if (!newFragments.find((f) => f.id === newFragment.id)) {
        newFragments.push(newFragment);
      }
    } else {
      // can only update fragments the user already owns
      newFragments = formInput.fragmentBalance.map((f) => (f.id !== newFragment.id ? f : newFragment));
    }
    setFormInput({ ...formInput, fragmentBalance: newFragments });
  };
  const handleAddItem = (newItem: { id: string; level: number }, type: string) => {
    if (type) {
      // @ts-ignore
      const newItems = formInput[type];
      newItems.push(newItem);
      setFormInput({ ...formInput, [type]: newItems });
    }
  };
  const handleAddAllItems = (items: { id: string; level: number }[], type: string) => {
    if (type) {
      // @ts-ignore
      const newItems = formInput[type].concat(items);
      setFormInput({ ...formInput, [type]: newItems });
    }
  };
  const handleDeleteItem = (itemToRemove: { id: string; level: number }, type: string) => {
    if (type) {
      // @ts-ignore
      const newItems = formInput[type];
      _.pullAllWith(newItems, [itemToRemove], _.isEqual);
      setFormInput({ ...formInput, [type]: newItems });
    }
  };
  const handleChangeItemLevel = (newItem: { id: string; level: number }, type: string) => {
    if (type) {
      // @ts-ignore
      const newItems = formInput[type].map((item) => (item.id === newItem.id ? newItem : item));
      setFormInput({ ...formInput, [type]: newItems });
    }
  };

  const handleSearchUrl = async (userid: string) => {
    setUpdateStatus(null);
    searchUser({ variables: { input: userid, type: 'BY_ID' } });
    getABTestsConfigs();
  };

  // @ts-ignore
  const handleKeyPressed = (target) => {
    if (target.charCode === 13) {
      target.preventDefault();
      handleSearch();
    }
  };

  const setNewUserFromBackend = useCallback((_data: SearchedUserWithAdminPlaylistProgress) => {
    const {
      id,
      userLevel,
      userLevelProgressToNextLevel: userProgressToNextLevel,
      coinBalance,
      // using the new energy systems object
      energySystems: {
        tournamentTicket: { extraBalance: tournamentTicketBalance, currentCapSupply: energyTournamentTicketSupply },
        liveEventTicket: { currentCapSupply: energyLiveEventTicketSupply },
        coin: { currentCapSupply: energyCoinSupply },
        xp: { currentCapSupply: energyXPSupply },
      },
      liveEventCurrency,
      diamondBalance,
      boostRemove2Balance,
      boostTeamSpiritBalance,
      boostReviveBalance,
      boostNinjaBalance,
      boostBombBalance,
      boostSpeedsterBalance,
      boostArtistOnlyBalance,
      boostHurdleHintBalance,
      stampCards,
      totalStampCardsEarned,
      unlimitedBoosts,
      basicTierContentPack,
      stickers: userStickers,
      appSkins: userAppSkins,
      profileFrames: userProfileFrames,
      fragmentBalance,
      currentUserMonthlyPass,
      ownedContentPacks,
      piggyBank,
      metaFragments,
      shards,
      accoladeBalance,
      team,
      freeSpins,
      calendars,
    } = _data;
    const savedRaw = _data.savedContentPacks?.list ?? [];
    const ownedPlaylistsList = savedRaw.filter((p) => p?.contentPack?.id).map((p) => mapUserPlaylistProgress(p));
    const recentRaw = _data.recentContentPacks?.list ?? [];
    const recentPlaylistsList = recentRaw.filter((p) => p?.contentPack?.id).map((p) => mapUserPlaylistProgress(p));

    const _earnedContentPackIds = ownedContentPacks.list.map(({ contentPack }) => contentPack.id);
    const newFormInput: FormInput = {
      id,
      userLevel,
      userProgressToNextLevel,
      coinBalance,
      diamondBalance,
      claimedUpdateGiftId: _data.claimedUpdateGiftId ?? '',
      tournamentTicketBalance: tournamentTicketBalance ?? undefined,
      boostRemove2Balance,
      boostTeamSpiritBalance,
      boostReviveBalance,
      boostNinjaBalance,
      boostBombBalance,
      boostSpeedsterBalance,
      boostArtistOnlyBalance,
      boostHurdleHintBalance,
      stampCards,
      totalStampCardsEarned,
      basicTierContentPack,
      savedContentPacks: ownedPlaylistsList,
      recentContentPacks: recentPlaylistsList,
      fragmentBalance,
      stickers: formatUserItems(userStickers, stickers.list),
      appSkins: formatUserItems(userAppSkins, appSkins.list),
      profileFrames: formatUserItems(userProfileFrames, profileFrames.list),
      energyCoinSupply,
      energyXPSupply,
      energyTournamentTicketSupply,
      energyLiveEventTicketSupply,
      liveEventCurrency,
      earnedContentPackIds: _earnedContentPackIds,
      // @ts-ignore
      piggyBank,
      metaFragments: {
        BRONZE: metaFragments.BRONZE?.amount ?? undefined,
        SILVER: metaFragments.SILVER?.amount ?? undefined,
        GOLD: metaFragments.GOLD?.amount ?? undefined,
      },
      shards: {
        BRONZE: shards.BRONZE ?? undefined,
        SILVER: shards.SILVER ?? undefined,
        GOLD: shards.GOLD ?? undefined,
      },
      accoladeBalance: accoladeBalance ?? 0,
      team: {
        id: team?.id ?? '',
      },
      freeSpins,
      calendars,
    };
    for (const [key, value] of Object.entries(unlimitedBoosts)) {
      // @ts-ignore
      newFormInput[`unlimitedBoosts.${key}`] = value;
    }
    if (userLevel >= 20 && currentUserMonthlyPass) {
      newFormInput.currentUserMonthlyPass = currentUserMonthlyPass;
    }
    setFormInput(newFormInput);
  }, [appSkins.list, profileFrames.list, stickers.list]);

  useEffect(() => {
    if (searchData && searchData.user) {
      setNewUserFromBackend(searchData.user);
      const { id } = searchData.user;
      getLastSession({ variables: { id } });
      saveCurrentSearchLocally(searchData);
      handleUserUpdated(searchData.user);
    }
  }, [searchData, handleUserUpdated, getLastSession, setNewUserFromBackend]);

  useEffect(() => {
    if (abTestConfigsData && abTestConfigsData?.liveOps) {
      const filtered = abTestConfigsData?.liveOps?.ABTestConfigs.filter(
        ({ status }) => status !== AbTestStatus.ARCHIVED
      );
      setAbTestConfigs(filtered);
    }
  }, [abTestConfigsData]);

  return {
    handleInputChange,
    handleChangeUser,
    handleSearch,
    handleSaveUser,
    handleSearchUrl,
    handleBanUser,
    handleUnbanUser,
    handleForceABTest,
    handleResetUserSubscriptions,
    handleUpdateSubscriptionExpires,
    updateLoading:
      updateLoading ||
      banLoading ||
      unbanLoading ||
      resetSubscriptionsLoading ||
      updateSubscriptionExpiresLoading ||
      savePlaylistLoading ||
      setCurrentBasicContentPackLoading ||
      updatePlaylistLoading ||
      unsavePlaylistLoading ||
      searchPlaylistLoading,
    resetSubscriptionsLoading,
    newUser: formInput,
    contentCategories,
    stickers: formInput ? getItemsNotOwnedByUser(formInput.stickers, stickers.list) : [],
    appSkins: formInput ? getItemsNotOwnedByUser(formInput.appSkins, appSkins.list) : [],
    profileFrames: formInput ? getItemsNotOwnedByUser(formInput.profileFrames, profileFrames.list) : [],
    data: user,
    energyCaps,
    input,
    handleChangeEarnedContentPackIds,
    handleChangeBasicPlaylist,
    handleSaveBasicPlaylist,
    handleChangeSavedPlaylist,
    handleSaveSavedPlaylist,
    handleChangeRecentPlaylist,
    handleSaveRecentPlaylist,
    handleChangeSearchedPlaylist,
    handleSaveSearchedPlaylist,
    handleSetSearchedPlaylistAsCurrentBasic,
    handleSearchPlaylist,
    handleDeleteSavedPlaylist,
    handleAddSavedPlaylist,
    searchedPlaylist,
    playlistUpdateMessage,
    handleChangeMonthlyPass,
    handleChangePiggyBank,
    handleChangeFragment,
    handleAddItem,
    handleAddAllItems,
    handleDeleteItem,
    handleChangeItemLevel,
    updateStatus,
    searchUserCalled,
    getUserLoading,
    abTestConfigs,
    appInfoLoading,
    lastSession: {
      data: sessionData && sessionData.analytics ? sessionData.analytics.userLastSession : null,
      loading: lastSessionLoading,
      called: lastSessionCalled,
    },
    handleKeyPressed,
    handleChangeMetaFragments,
    handleChangeShards,
  };
};
